import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const UsersManagement = () => {
  const [jobSeekers, setJobSeekers] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // all, jobseeker, employer

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = () => {
    const seekers = JSON.parse(localStorage.getItem('jobSeekers') || '[]');
    const emps = JSON.parse(localStorage.getItem('employers') || '[]');
    setJobSeekers(seekers);
    setEmployers(emps);
  };

  const getAllUsers = () => {
    const allUsers = [
      ...jobSeekers.map(user => ({ ...user, role: 'jobseeker' })),
      ...employers.map(user => ({ ...user, role: 'employer' }))
    ];
    return allUsers.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  };

  const getFilteredUsers = () => {
    const allUsers = getAllUsers();
    switch (activeTab) {
      case 'jobseeker':
        return allUsers.filter(u => u.role === 'jobseeker');
      case 'employer':
        return allUsers.filter(u => u.role === 'employer');
      default:
        return allUsers;
    }
  };

  const handleRemoveUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const confirmRemoveUser = () => {
    if (!selectedUser) return;

    if (selectedUser.role === 'jobseeker') {
      const updatedSeekers = jobSeekers.filter(u => u.email !== selectedUser.email);
      localStorage.setItem('jobSeekers', JSON.stringify(updatedSeekers));
      setJobSeekers(updatedSeekers);
    } else if (selectedUser.role === 'employer') {
      const updatedEmployers = employers.filter(u => u.email !== selectedUser.email);
      localStorage.setItem('employers', JSON.stringify(updatedEmployers));
      setEmployers(updatedEmployers);
    }

    setShowDeleteModal(false);
    setSelectedUser(null);
  };

  const handleChangeRole = (user) => {
    setSelectedUser(user);
    setNewRole(user.role === 'jobseeker' ? 'employer' : 'jobseeker');
    setShowRoleModal(true);
  };

  const confirmChangeRole = () => {
    if (!selectedUser) return;

    // Remove from current role
    if (selectedUser.role === 'jobseeker') {
      const updatedSeekers = jobSeekers.filter(u => u.email !== selectedUser.email);
      localStorage.setItem('jobSeekers', JSON.stringify(updatedSeekers));
      setJobSeekers(updatedSeekers);

      // Add to employer
      const userAsEmployer = { ...selectedUser, role: 'employer' };
      delete userAsEmployer.role; // Remove role field as it's tracked by storage location
      const updatedEmployers = [...employers, userAsEmployer];
      localStorage.setItem('employers', JSON.stringify(updatedEmployers));
      setEmployers(updatedEmployers);
    } else if (selectedUser.role === 'employer') {
      const updatedEmployers = employers.filter(u => u.email !== selectedUser.email);
      localStorage.setItem('employers', JSON.stringify(updatedEmployers));
      setEmployers(updatedEmployers);

      // Add to job seeker
      const userAsSeeker = { ...selectedUser, role: 'jobseeker' };
      delete userAsSeeker.role; // Remove role field as it's tracked by storage location
      const updatedSeekers = [...jobSeekers, userAsSeeker];
      localStorage.setItem('jobSeekers', JSON.stringify(updatedSeekers));
      setJobSeekers(updatedSeekers);
    }

    setShowRoleModal(false);
    setSelectedUser(null);
    setNewRole('');
  };

  const filteredUsers = getFilteredUsers();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-green-50 to-blue-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 via-teal-600 to-cyan-600 rounded-2xl shadow-2xl p-8 mb-8 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-10 rounded-full -ml-24 -mb-24"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center mb-3">
                  <span className="text-5xl mr-4">👥</span>
                  <h1 className="text-4xl font-bold">
                    Users Management
                  </h1>
                </div>
                <p className="text-green-100 text-lg">
                  Manage all platform users, change roles, or remove accounts
                </p>
              </div>
              <Link
                to="/admin/dashboard"
                className="bg-white text-green-600 px-6 py-3 rounded-xl hover:bg-green-50 font-semibold transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                ← Back to Dashboard
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium mb-1">Total Users</p>
                <p className="text-4xl font-bold">
                  {jobSeekers.length + employers.length}
                </p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <div className="text-5xl">👥</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium mb-1">Job Seekers</p>
                <p className="text-4xl font-bold">{jobSeekers.length}</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <div className="text-5xl">👤</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium mb-1">Employers</p>
                <p className="text-4xl font-bold">{employers.length}</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <div className="text-5xl">💼</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 mb-8 border border-gray-100">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all shadow-md hover:shadow-lg transform hover:scale-105 ${
                activeTab === 'all'
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Users ({jobSeekers.length + employers.length})
            </button>
            <button
              onClick={() => setActiveTab('jobseeker')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all shadow-md hover:shadow-lg transform hover:scale-105 ${
                activeTab === 'jobseeker'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              👤 Job Seekers ({jobSeekers.length})
            </button>
            <button
              onClick={() => setActiveTab('employer')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all shadow-md hover:shadow-lg transform hover:scale-105 ${
                activeTab === 'employer'
                  ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              💼 Employers ({employers.length})
            </button>
          </div>
        </div>

        {/* Users List */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          {filteredUsers.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <div className="text-5xl mb-4">👥</div>
              <p className="text-gray-500 text-lg">No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      User
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Email
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Role
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      Joined
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredUsers.map((user, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {user.photoURL ? (
                            <img
                              src={user.photoURL}
                              alt={user.displayName || user.name}
                              className="w-10 h-10 rounded-full mr-3"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                              <span className="text-blue-600 font-semibold text-lg">
                                {(user.displayName || user.name || user.email || 'U')[0].toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-gray-800">
                              {user.displayName || user.name || 'Unknown User'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-gray-700">{user.email}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            user.role === 'jobseeker'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                          }`}
                        >
                          {user.role === 'jobseeker' ? '👤 Job Seeker' : '💼 Employer'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-gray-600 text-sm">
                          {user.createdAt
                            ? new Date(user.createdAt).toLocaleDateString()
                            : 'N/A'}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleChangeRole(user)}
                            className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 font-semibold text-sm transition-all"
                          >
                            🔄 Change Role
                          </button>
                          <button
                            onClick={() => handleRemoveUser(user)}
                            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 font-semibold text-sm transition-all"
                          >
                            🗑️ Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="text-center mb-6">
                <div className="text-5xl mb-4">⚠️</div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Remove User?
                </h2>
                <p className="text-gray-600 mb-4">
                  Are you sure you want to remove this user? This action cannot be undone.
                </p>
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="font-semibold text-gray-800">
                    {selectedUser.displayName || selectedUser.name}
                  </p>
                  <p className="text-sm text-gray-600">{selectedUser.email}</p>
                  <p className="text-sm text-gray-600 mt-2">
                    Role: <span className="font-semibold">{selectedUser.role}</span>
                  </p>
                </div>
              </div>
              <div className="bg-yellow-50 p-3 rounded-lg mb-4">
                <p className="text-sm text-yellow-800">
                  ℹ️ The user will need to log in with Google again and select a new role to
                  access the platform.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedUser(null);
                  }}
                  className="flex-1 bg-gray-400 text-white px-6 py-3 rounded-lg hover:bg-gray-500 font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRemoveUser}
                  className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 font-semibold transition-all"
                >
                  Yes, Remove User
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Change Role Confirmation Modal */}
        {showRoleModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="text-center mb-6">
                <div className="text-5xl mb-4">🔄</div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Change User Role?
                </h2>
                <p className="text-gray-600 mb-4">
                  Are you sure you want to change this user's role?
                </p>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="font-semibold text-gray-800">
                    {selectedUser.displayName || selectedUser.name}
                  </p>
                  <p className="text-sm text-gray-600">{selectedUser.email}</p>
                  <div className="flex items-center justify-center gap-3 mt-3">
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        selectedUser.role === 'jobseeker'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {selectedUser.role === 'jobseeker' ? '👤 Job Seeker' : '💼 Employer'}
                    </span>
                    <span className="text-2xl">→</span>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        newRole === 'jobseeker'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {newRole === 'jobseeker' ? '👤 Job Seeker' : '💼 Employer'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRoleModal(false);
                    setSelectedUser(null);
                    setNewRole('');
                  }}
                  className="flex-1 bg-gray-400 text-white px-6 py-3 rounded-lg hover:bg-gray-500 font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmChangeRole}
                  className="flex-1 bg-yellow-600 text-white px-6 py-3 rounded-lg hover:bg-yellow-700 font-semibold transition-all"
                >
                  Yes, Change Role
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersManagement;
